import {
  GS1_AI,
  buildGs1DigitalLinkUrl,
  buildQualifierDocumentId,
  encodeCanonicalGs1Value,
} from '@myplayer/gs1';
import {
  IdentifierDraftSchema,
  type DraftTargetLink,
  type IdentifierDraft,
  type IdentifierStatus,
  type QualifierCombinationCode,
  type ValidationBlock,
} from '@myplayer/schema';
import { validateQualifierCandidate } from '@myplayer/validation';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';
import { formatPlainValidationError } from './products-firestore.js';

export type SupportedQualifierAi =
  typeof GS1_AI.BATCH_LOT | typeof GS1_AI.SERIAL | typeof GS1_AI.CPV;

export interface QualifierTypeOption {
  readonly code: SupportedQualifierAi;
  readonly label: string;
  readonly shortLabel: string;
  readonly placeholder: string;
}

/**
 * Supported single-AI product qualifier overrides on the product screen:
 * Batch/lot (`10`), Serial (`21`), and CPV (`22`).
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.4, p23 & §4.8, p25;
 * GS1-Conformant Resolver Standard v1.2.1 §2.5.10, pp21–22.
 */
export const PRODUCT_QUALIFIER_OPTIONS: readonly QualifierTypeOption[] = [
  {
    code: GS1_AI.BATCH_LOT,
    label: `Batch / lot (${GS1_AI.BATCH_LOT})`,
    shortLabel: `Batch/lot (${GS1_AI.BATCH_LOT})`,
    placeholder: 'LOT2026A',
  },
  {
    code: GS1_AI.SERIAL,
    label: `Serial (${GS1_AI.SERIAL})`,
    shortLabel: `Serial (${GS1_AI.SERIAL})`,
    placeholder: 'SN000123',
  },
  {
    code: GS1_AI.CPV,
    label: `CPV (${GS1_AI.CPV})`,
    shortLabel: `CPV (${GS1_AI.CPV})`,
    placeholder: 'V2',
  },
] as const;

export interface QualifierOverrideRecord {
  readonly qualifierKey: string;
  readonly identifierKey: string;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly combinationCode: QualifierCombinationCode;
  readonly qualifiers: Readonly<Record<string, string>>;
  readonly label?: string;
  readonly status?: IdentifierStatus;
  readonly defaultUrl?: string;
  readonly links?: readonly DraftTargetLink[];
  readonly version?: number;
  readonly validation?: ValidationBlock;
  readonly draft?: IdentifierDraft;
  readonly createdBy?: string;
  readonly updatedBy?: string;
}

export function resolveQualifierOption(code: string): QualifierTypeOption {
  return (
    PRODUCT_QUALIFIER_OPTIONS.find((opt) => opt.code === code) ?? {
      code: GS1_AI.BATCH_LOT,
      label: `Qualifier (${code})`,
      shortLabel: `Qualifier (${code})`,
      placeholder: 'VAL01',
    }
  );
}

export function resolveQualifierEffectiveStatus(record: QualifierOverrideRecord): IdentifierStatus {
  return record.draft?.status ?? record.status ?? 'ACTIVE';
}

export function isQualifierOverrideActive(record: QualifierOverrideRecord): boolean {
  return resolveQualifierEffectiveStatus(record) !== 'ARCHIVED';
}

export function resolveQualifierPrimaryPair(record: QualifierOverrideRecord): {
  readonly code: SupportedQualifierAi;
  readonly value: string;
} {
  for (const opt of PRODUCT_QUALIFIER_OPTIONS) {
    const val = record.qualifiers[opt.code];
    if (typeof val === 'string' && val.length > 0) {
      return { code: opt.code, value: val };
    }
  }
  const firstEntry = Object.entries(record.qualifiers)[0];
  return {
    code: (firstEntry?.[0] as SupportedQualifierAi | undefined) ?? GS1_AI.BATCH_LOT,
    value: firstEntry?.[1] ?? '',
  };
}

export function resolveQualifierDisplayUrl(record: QualifierOverrideRecord): string {
  const draftLinks = record.draft?.links?.filter((l) => (l as DraftTargetLink).removed !== true);
  const draftDefault = draftLinks?.find((l) => l.defaultLink)?.href ?? record.draft?.defaultUrl;
  if (typeof draftDefault === 'string' && draftDefault.trim().length > 0) {
    return draftDefault.trim();
  }
  const liveDefault = record.links?.find((l) => l.defaultLink)?.href ?? record.defaultUrl;
  return typeof liveDefault === 'string' ? liveDefault.trim() : '';
}

export function resolveQualifierDisplayTitle(record: QualifierOverrideRecord): string {
  const pair = resolveQualifierPrimaryPair(record);
  const defaultTitle = `${resolveQualifierOption(pair.code).shortLabel} ${pair.value}`.trim();
  const candidate = record.draft?.title ?? record.label ?? defaultTitle;
  return candidate.trim().length > 0 ? candidate.trim() : defaultTitle;
}

export function buildQualifierDigitalLinkPreviewUrl(params: {
  readonly host: string;
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly qualifierCode: SupportedQualifierAi;
  readonly qualifierValue: string;
}): string {
  const trimmedVal = params.qualifierValue.trim();
  if (trimmedVal.length === 0) return '';
  try {
    return buildGs1DigitalLinkUrl({
      host: params.host,
      ai: params.parentAi,
      primaryKey: params.parentPrimaryKey,
      qualifiers: { [params.qualifierCode]: trimmedVal },
    });
  } catch {
    return `/${params.parentAi}/${encodeCanonicalGs1Value(params.parentPrimaryKey)}/${params.qualifierCode}/${encodeCanonicalGs1Value(trimmedVal)}`;
  }
}

export interface ValidatedQualifierOverride {
  readonly ok: true;
  readonly qualifierKey: string;
  readonly combinationCode: QualifierCombinationCode;
  readonly qualifiers: Readonly<Record<string, string>>;
  readonly title: string;
  readonly defaultUrl: string;
  readonly links: readonly DraftTargetLink[];
}

export function validateQualifierOverrideInput(params: {
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly qualifierCode: SupportedQualifierAi;
  readonly qualifierValue: string;
  readonly targetUrl: string;
  readonly label?: string;
}): ValidatedQualifierOverride | { readonly ok: false; readonly message: string } {
  const cleanValue = params.qualifierValue.trim();
  if (cleanValue.length === 0) {
    return {
      ok: false,
      message: `Enter a ${resolveQualifierOption(params.qualifierCode).shortLabel} value.`,
    };
  }
  const cleanUrl = params.targetUrl.trim();
  if (cleanUrl.length === 0) {
    return {
      ok: false,
      message: 'Enter the destination web address (https://...) for this qualifier override.',
    };
  }

  const combinationCode = params.qualifierCode as QualifierCombinationCode;
  const qualifiers: Readonly<Record<string, string>> = { [params.qualifierCode]: cleanValue };
  const defaultTitle = `${resolveQualifierOption(params.qualifierCode).shortLabel} ${cleanValue}`;
  const cleanTitle =
    params.label !== undefined && params.label.trim().length > 0
      ? params.label.trim()
      : defaultTitle;
  const links: readonly DraftTargetLink[] = [
    {
      linkType: 'gs1:pip',
      href: cleanUrl,
      title: cleanTitle,
      type: 'text/html',
      hreflang: ['en'],
      context: ['GLOBAL'],
      defaultLink: true,
    },
  ];

  const validation = validateQualifierCandidate(
    params.parentAi,
    params.parentPrimaryKey,
    qualifiers,
    cleanUrl,
    links,
    { requireSingleDefaultLink: true },
  );
  if (validation.status === 'INVALID') {
    const firstErr = validation.errors[0];
    return {
      ok: false,
      message: firstErr
        ? formatPlainValidationError(firstErr)
        : 'Qualifier override failed GS1 or URL safety validation.',
    };
  }

  return {
    ok: true,
    qualifierKey: buildQualifierDocumentId(combinationCode, qualifiers),
    combinationCode,
    qualifiers,
    title: cleanTitle,
    defaultUrl: cleanUrl,
    links,
  };
}

function buildValidatedQualifierDraft(
  check: ValidatedQualifierOverride,
  uid: string,
  status: IdentifierStatus = 'ACTIVE',
): IdentifierDraft {
  const draftCandidate: IdentifierDraft = {
    title: check.title,
    status,
    defaultUrl: check.defaultUrl,
    links: check.links,
    rules: [],
    submittedBy: uid,
    submittedAt: new Date().toISOString(),
  };
  const parsedDraft = IdentifierDraftSchema.safeParse(draftCandidate);
  if (!parsedDraft.success) {
    throw new Error(parsedDraft.error.issues[0]?.message ?? 'Invalid qualifier draft schema');
  }
  return draftCandidate;
}

export async function createStagedQualifierInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly uid: string;
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly qualifierCode: SupportedQualifierAi;
  readonly qualifierValue: string;
  readonly targetUrl: string;
  readonly label?: string;
}): Promise<{ readonly qualifierKey: string; readonly draft: IdentifierDraft }> {
  const check = validateQualifierOverrideInput(params);
  if (!check.ok) throw new Error(check.message);

  const draft = buildValidatedQualifierDraft(check, params.uid, 'ACTIVE');
  const qualifierRef = doc(
    params.db,
    'organisations',
    params.orgId,
    'workspaces',
    params.workspaceId,
    'identifiers',
    params.identifierKey,
    'qualifiers',
    check.qualifierKey,
  );

  await setDoc(qualifierRef, {
    qualifierKey: check.qualifierKey,
    identifierKey: params.identifierKey,
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    combinationCode: check.combinationCode,
    qualifiers: check.qualifiers,
    createdBy: params.uid,
    createdAt: serverTimestamp(),
    updatedBy: params.uid,
    updatedAt: serverTimestamp(),
    draft,
  });

  return { qualifierKey: check.qualifierKey, draft };
}

export async function saveStagedQualifierDraftInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly qualifierKey: string;
  readonly uid: string;
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly qualifierCode: SupportedQualifierAi;
  readonly qualifierValue: string;
  readonly targetUrl: string;
  readonly label?: string;
  readonly status?: IdentifierStatus;
}): Promise<IdentifierDraft> {
  const check = validateQualifierOverrideInput(params);
  if (!check.ok) throw new Error(check.message);

  const nextDraft = buildValidatedQualifierDraft(check, params.uid, params.status ?? 'ACTIVE');
  const qualifierRef = doc(
    params.db,
    'organisations',
    params.orgId,
    'workspaces',
    params.workspaceId,
    'identifiers',
    params.identifierKey,
    'qualifiers',
    params.qualifierKey,
  );

  await updateDoc(qualifierRef, {
    draft: nextDraft,
    updatedBy: params.uid,
    updatedAt: serverTimestamp(),
  });
  return nextDraft;
}

export async function upsertStagedQualifierInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly uid: string;
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly qualifierCode: SupportedQualifierAi;
  readonly qualifierValue: string;
  readonly targetUrl: string;
  readonly label?: string;
  readonly existingQualifierKeys?: ReadonlySet<string>;
}): Promise<{ readonly qualifierKey: string; readonly draft: IdentifierDraft }> {
  const check = validateQualifierOverrideInput(params);
  if (!check.ok) throw new Error(check.message);

  let exists = params.existingQualifierKeys?.has(check.qualifierKey) ?? false;
  if (!exists && params.existingQualifierKeys === undefined) {
    const snap = await getDoc(
      doc(
        params.db,
        'organisations',
        params.orgId,
        'workspaces',
        params.workspaceId,
        'identifiers',
        params.identifierKey,
        'qualifiers',
        check.qualifierKey,
      ),
    );
    exists = snap.exists();
  }

  if (exists) {
    const draft = await saveStagedQualifierDraftInFirestore({
      ...params,
      qualifierKey: check.qualifierKey,
      status: 'ACTIVE',
    });
    return { qualifierKey: check.qualifierKey, draft };
  }
  return createStagedQualifierInFirestore(params);
}

export async function removeStagedQualifierInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly uid: string;
  readonly parentAi: string;
  readonly parentPrimaryKey: string;
  readonly record: QualifierOverrideRecord;
}): Promise<IdentifierDraft> {
  const pair = resolveQualifierPrimaryPair(params.record);
  const existingUrl =
    resolveQualifierDisplayUrl(params.record) || 'https://brand.example.com/archived';
  const existingTitle = resolveQualifierDisplayTitle(params.record);

  return saveStagedQualifierDraftInFirestore({
    db: params.db,
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    identifierKey: params.identifierKey,
    qualifierKey: params.record.qualifierKey,
    uid: params.uid,
    parentAi: params.parentAi,
    parentPrimaryKey: params.parentPrimaryKey,
    qualifierCode: pair.code,
    qualifierValue: pair.value,
    targetUrl: existingUrl,
    label: existingTitle,
    status: 'ARCHIVED',
  });
}
